import { useState, useEffect } from 'react';
import { paidLeaveService, staffService, PaidLeave, Staff } from '@/lib/storage';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  CheckCircle2,
  XCircle,
  Clock,
  Filter,
  Search,
  CalendarOff,
  Loader2,
  Trash2,
} from 'lucide-react';
import { cn } from '@/lib/utils';

const statusConfig: Record<string, { label: string; className: string }> = {
  pending: { label: 'Pending', className: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
  approved: { label: 'Approved', className: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' },
  rejected: { label: 'Rejected', className: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400' },
};

export default function AdminPaidLeavesPage() {
  const [leaves, setLeaves] = useState<PaidLeave[]>([]);
  const [staffList, setStaffList] = useState<Staff[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [staffFilter, setStaffFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [reviewDialog, setReviewDialog] = useState<PaidLeave | null>(null);
  const [reviewStatus, setReviewStatus] = useState<'approved' | 'rejected'>('approved');
  const [adminRemark, setAdminRemark] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    loadData();
    const interval = setInterval(() => loadData(true), 10000);
    return () => clearInterval(interval);
  }, []);

  const loadData = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [leavesData, staffData] = await Promise.all([
        paidLeaveService.getAll(),
        staffService.getAll(),
      ]);
      setLeaves(leavesData);
      setStaffList(staffData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const filteredLeaves = leaves.filter((l) => {
    if (statusFilter !== 'all' && l.status !== statusFilter) return false;
    if (staffFilter !== 'all' && l.staffId !== staffFilter) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return (
        l.staffName?.toLowerCase().includes(q) ||
        l.reason?.toLowerCase().includes(q)
      );
    }
    return true;
  });

  const handleReview = async () => {
    if (!reviewDialog) return;
    setProcessing(true);
    try {
      await paidLeaveService.updateStatus(reviewDialog.id, reviewStatus, adminRemark || undefined);
      setReviewDialog(null);
      setAdminRemark('');
      await loadData();
    } catch (error) {
      console.error('Error reviewing paid leave:', error);
    } finally {
      setProcessing(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this paid leave request?')) return;
    try {
      await paidLeaveService.delete(id);
      await loadData();
    } catch (error) {
      console.error('Error deleting paid leave:', error);
    }
  };

  const pendingCount = leaves.filter((l) => l.status === 'pending').length;
  const approvedCount = leaves.filter((l) => l.status === 'approved').length;
  const rejectedCount = leaves.filter((l) => l.status === 'rejected').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white">Paid Leave Requests</h1>
          <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-0.5 sm:mt-1">Review and manage staff paid leave applications</p>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 sm:gap-4">
        <Card className="bg-white/50 dark:bg-slate-900/40 border-amber-200/50 dark:border-amber-800/30">
          <CardContent className="p-2.5 sm:p-4 flex flex-col sm:flex-row items-start sm:items-center gap-1.5 sm:gap-3 min-w-0">
            <div className="p-1.5 sm:p-2 rounded-xl bg-amber-500/10 shrink-0">
              <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600 dark:text-amber-400" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-base sm:text-2xl font-black text-amber-600 dark:text-amber-400 truncate">{pendingCount}</p>
              <p className="text-[9px] sm:text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide truncate">Pending</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white/50 dark:bg-slate-900/40 border-emerald-200/50 dark:border-emerald-800/30">
          <CardContent className="p-2.5 sm:p-4 flex flex-col sm:flex-row items-start sm:items-center gap-1.5 sm:gap-3 min-w-0">
            <div className="p-1.5 sm:p-2 rounded-xl bg-emerald-500/10 shrink-0">
              <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-base sm:text-2xl font-black text-emerald-600 dark:text-emerald-400 truncate">{approvedCount}</p>
              <p className="text-[9px] sm:text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide truncate">Approved</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white/50 dark:bg-slate-900/40 border-rose-200/50 dark:border-rose-800/30">
          <CardContent className="p-2.5 sm:p-4 flex flex-col sm:flex-row items-start sm:items-center gap-1.5 sm:gap-3 min-w-0">
            <div className="p-1.5 sm:p-2 rounded-xl bg-rose-500/10 shrink-0">
              <XCircle className="w-4 h-4 sm:w-5 sm:h-5 text-rose-600 dark:text-rose-400" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-base sm:text-2xl font-black text-rose-600 dark:text-rose-400 truncate">{rejectedCount}</p>
              <p className="text-[9px] sm:text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide truncate">Rejected</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="bg-white/50 dark:bg-slate-900/40 overflow-hidden">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-3">
            <div className="relative flex-1 max-w-md w-full">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by name or reason..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 h-11 rounded-xl bg-white/50 dark:bg-slate-950/40 border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-slate-300 dark:focus:ring-slate-700 transition-all font-medium text-sm w-full"
              />
            </div>
            <div className="flex gap-2">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[140px]">
                  <Filter className="w-3 h-3 mr-1" />
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
              <Select value={staffFilter} onValueChange={setStaffFilter}>
                <SelectTrigger className="w-[160px]">
                  <SelectValue placeholder="Staff" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Staff</SelectItem>
                  {staffList.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white/50 dark:bg-slate-900/40">
        <CardContent className="p-0">
          {filteredLeaves.length === 0 ? (
            <div className="text-center py-12">
              <CalendarOff className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
              <p className="text-sm font-medium text-slate-500 dark:text-slate-400">No paid leave requests found</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Staff</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Admin Remark</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredLeaves.map((leave) => (
                  <TableRow key={leave.id}>
                    <TableCell className="font-medium">{leave.staffName || 'Unknown'}</TableCell>
                    <TableCell>{new Date(leave.date + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</TableCell>
                    <TableCell className="max-w-[200px] truncate text-slate-500 dark:text-slate-400">{leave.reason || '-'}</TableCell>
                    <TableCell>
                      <span className={cn('px-2 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wide', statusConfig[leave.status]?.className)}>
                        {statusConfig[leave.status]?.label}
                      </span>
                    </TableCell>
                    <TableCell className="max-w-[150px] truncate text-slate-500 dark:text-slate-400 text-sm">{leave.adminRemark || '-'}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {leave.status === 'pending' && (
                          <Button
                            size="sm"
                            onClick={() => {
                              setReviewDialog(leave);
                              setReviewStatus('approved');
                              setAdminRemark('');
                            }}
                            className="h-7 px-2 text-[10px] font-bold bg-emerald-600 hover:bg-emerald-700 text-white"
                          >
                            <CheckCircle2 className="w-3 h-3 mr-1" /> Review
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDelete(leave.id)}
                          className="h-7 px-2 text-[10px] font-bold text-rose-600 hover:text-rose-700 hover:bg-rose-50 dark:hover:bg-rose-900/20"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!reviewDialog} onOpenChange={() => setReviewDialog(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Review Paid Leave</DialogTitle>
            <DialogDescription>
              {reviewDialog?.staffName} — {reviewDialog ? new Date(reviewDialog.date + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' }) : ''}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {reviewDialog?.reason && (
              <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-900/50">
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wide">Reason</p>
                <p className="text-sm text-slate-700 dark:text-slate-300 mt-1">{reviewDialog.reason}</p>
              </div>
            )}
            <div className="flex gap-2">
              <Button
                variant={reviewStatus === 'approved' ? 'default' : 'outline'}
                onClick={() => setReviewStatus('approved')}
                className={cn('flex-1', reviewStatus === 'approved' && 'bg-emerald-600 hover:bg-emerald-700')}
              >
                <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
              </Button>
              <Button
                variant={reviewStatus === 'rejected' ? 'default' : 'outline'}
                onClick={() => setReviewStatus('rejected')}
                className={cn('flex-1', reviewStatus === 'rejected' && 'bg-rose-600 hover:bg-rose-700')}
              >
                <XCircle className="w-4 h-4 mr-1" /> Reject
              </Button>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wide text-slate-500">Admin Remark (optional)</Label>
              <Textarea
                placeholder="Add a remark..."
                value={adminRemark}
                onChange={(e) => setAdminRemark(e.target.value)}
                rows={2}
                className="rounded-xl border-slate-200 dark:border-slate-800"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewDialog(null)}>Cancel</Button>
            <Button onClick={handleReview} disabled={processing} className="bg-slate-900 dark:bg-white text-white dark:text-slate-900">
              {processing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              Confirm {reviewStatus === 'approved' ? 'Approval' : 'Rejection'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
